/**
 * Entscheidet, ob ein persistierter XState-State wiederaufgenommen werden darf (UCPWO-398).
 *
 * Ein wiederaufgenommener State bringt sein `value` mit — die Machine startet also mitten in
 * ihrem Ablauf und überspringt den Zustand, der lädt. Ist der persistierte Context dabei
 * unvollständig, lädt nichts nach: `sessionStorage` überlebt Vollreloads, und
 * `usePersistedMachine` räumt den Eintrag nur beim Unmount auf. Ein solcher Eintrag macht die
 * Seite deshalb für die restliche Tab-Sitzung kaputt, und zwar reproduzierbar.
 *
 * Beobachtet auf der Kalkulationsseite: `financeMachine.init` ist `type: 'parallel'` mit
 * `onDone: 'idle'`, ein gescheitertes `getSubproject` erreicht also trotzdem `idle`. Genau
 * dieser Zustand wurde persistiert — danach fehlte der Schadenbericht-Block und die
 * Angebots-/Kalkulationslisten blieben leer, bis der Eintrag verschwand.
 *
 * `canResume` lässt den Aufrufer solche Zustände verwerfen; die Machine startet dann normal in
 * ihrem Initial-State und lädt. Das ist derselbe Pfad wie beim ersten Aufruf ohne
 * persistierten Eintrag — deshalb ist der schlimmste Fall eines zu strengen `canResume` nur
 * ein Ladevorgang mehr.
 *
 * Bewusst frei von React- und Browser-Importen, damit die Entscheidung als reine Logik
 * testbar bleibt (`*.test.ts` läuft hier in node, siehe `vitest.config.ts`).
 */
export const readResumableState = (
    raw: string | null,
    canResume?: (persisted: any) => boolean
): any | undefined => {
    if (!raw) return undefined;

    let parsed: any;
    try {
        parsed = JSON.parse(raw);
    } catch {
        // Ein zerschossener Eintrag darf die Seite nicht mitnehmen — vorher warf `JSON.parse`
        // im Aufrufer ungefangen und riss den Render mit.
        return undefined;
    }

    if (!parsed) return undefined;
    return canResume && !canResume(parsed) ? undefined : parsed;
};
